import { type CSSProperties } from 'react';
import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import NovusLogo from '@assets/icons/novusLogo.svg?component';
import { useCustomer } from '@contexts/CustomerContext';
import type { CustomerLogoProps } from './CustomerLogo.types';

const isUrl = (value: string): boolean =>
  value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/');

/**
 * Renders the tenant logo. When `branding.logoSrc` (or the `logoSrc` prop) is a
 * URL it renders an `<img>`; otherwise it falls back to the bundled Novus SVG.
 * Reads branding from context so it re-themes with the tenant config.
 *
 * The `<img>` branch's own shrink-to-fit default lives in `sx` (a real emitted
 * CSS class, subject to normal cascade/specificity), never in `style` (an inline
 * style attribute). Inline styles win over ANY class-based rule regardless of
 * specificity — an inline `max-height` here would permanently shadow the per-consumer
 * caps in novus.tokens.ts no matter how their selectors are written, since a
 * consumer's theme rule can never be "more inline" than an inline style.
 *
 * The className this component receives (`NavTopBar-logo` / `CheckoutHeader-logo`)
 * lands directly on this `<img>` itself — there is no wrapper for it to be a
 * descendant of. novus.tokens.ts's actual per-consumer caps are therefore keyed on
 * the compound tag+class selectors `img.NavTopBar-logo` / `img.CheckoutHeader-logo`,
 * NOT a `.NavTopBar-logo img` / `.CheckoutHeader-logo img` descendant selector (that
 * form matches nothing here — it's the right pattern for `.AddressHeader-heroImage
 * img`, where the class sits on a wrapping Box with the img nested inside, but a
 * different case from this component). `style` is left purely for a caller-supplied
 * override, which SHOULD win unconditionally as an explicit instance override.
 */
export const CustomerLogo = ({
  logoSrc: logoSrcProp,
  sx,
  style,
  className,
  ...svgProps
}: CustomerLogoProps): JSX.Element => {
  const { branding } = useCustomer();
  const src = logoSrcProp ?? branding.logoSrc;
  const alt = branding.displayName ? `${branding.displayName} logo` : 'logo';

  if (src && isUrl(src)) {
    const defaultSx = {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto',
    };
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        className={className}
        sx={[defaultSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        style={style as CSSProperties}
      />
    );
  }

  return <SvgIcon component={NovusLogo} inheritViewBox sx={sx} style={style} className={className} {...svgProps} />;
};
